import { Component } from '@angular/core';
import { Router } from '@angular/router';

@Component({
  selector: 'app-unauthorized',
  template: `
    <div
      class="min-h-screen flex items-center justify-center bg-gradient-to-br from-sacred to-elegant dark:from-stone-900 dark:to-stone-800 p-4 relative overflow-hidden"
    >
      <!-- Sacred floating orbs -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          class="absolute top-20 left-20 w-32 h-32 bg-bloom/20 dark:bg-fuchsia-500/20 rounded-full blur-2xl animate-float opacity-60"
        ></div>
        <div
          class="absolute top-40 right-32 w-24 h-24 bg-soulfire/15 dark:bg-fuchsia-400/15 rounded-full blur-xl animate-float-delayed opacity-50"
        ></div>
        <div
          class="absolute bottom-32 left-1/4 w-20 h-20 bg-grounded/20 dark:bg-stone-600/20 rounded-full blur-lg animate-float opacity-40"
        ></div>
        <div
          class="absolute bottom-20 right-1/3 w-40 h-40 bg-bloom/10 dark:bg-fuchsia-500/10 rounded-full blur-3xl animate-float-delayed opacity-70"
        ></div>
        <div
          class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-soulfire/5 dark:bg-fuchsia-400/5 rounded-full blur-3xl animate-pulse"
        ></div>
      </div>

      <!-- Sacred geometric patterns -->
      <div class="absolute inset-0 opacity-5 dark:opacity-10">
        <div class="grid-pattern dark:grid-pattern-dark"></div>
      </div>

      <!-- Main sacred vessel -->
      <div class="relative z-10 max-w-lg w-full">
        <div
          class="bg-white/90 dark:bg-stone-800/90 backdrop-blur-xl rounded-3xl shadow-2xl dark:shadow-fuchsia-900/10 p-10 text-center transform animate-slideUp border border-grounded/20 dark:border-stone-700/50 relative overflow-hidden"
        >
          <!-- Sacred energy rings -->
          <div class="absolute inset-0 pointer-events-none">
            <div
              class="absolute top-4 right-4 w-3 h-3 bg-bloom dark:bg-fuchsia-400 rounded-full animate-pulse"
            ></div>
            <div
              class="absolute bottom-4 left-4 w-2 h-2 bg-soulfire dark:bg-fuchsia-500 rounded-full animate-pulse animation-delay-500"
            ></div>
            <div
              class="absolute top-1/3 left-6 w-1 h-1 bg-grounded dark:bg-stone-400 rounded-full animate-pulse animation-delay-300"
            ></div>
          </div>

          <!-- Sacred icon -->
          <div class="mb-8 relative">
            <div
              class="w-28 h-28 mx-auto bg-gradient-to-br from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 rounded-full flex items-center justify-center animate-bounceIn shadow-2xl shadow-soulfire/30 dark:shadow-fuchsia-500/30 relative"
            >
              <div class="text-5xl text-white animate-pulse">🔮</div>
              <!-- Energy emanation -->
              <div
                class="absolute inset-0 rounded-full bg-gradient-to-br from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 opacity-20 animate-ping"
              ></div>
            </div>
            <!-- Sacred geometric ring -->
            <div
              class="absolute inset-0 w-28 h-28 mx-auto rounded-full border-2 border-bloom/30 dark:border-fuchsia-400/30 animate-spin-slow"
              style="animation-duration: 20s;"
            ></div>
          </div>

          <h1
            class="text-4xl font-bold bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-400 dark:to-fuchsia-300 bg-clip-text text-transparent mb-4 animate-fadeInUp font-sacramento"
          >
            Sacred Boundary
          </h1>

          <h2
            class="text-xl font-semibold text-rooted dark:text-stone-100 mb-6 animate-fadeInUp animation-delay-200"
          >
            This Sacred Space Awaits Your Permission
          </h2>

          <p
            class="text-rooted/70 dark:text-stone-300 mb-10 animate-fadeInUp animation-delay-400 leading-relaxed text-lg"
          >
            The cosmos has gently guided you to a threshold that requires divine
            authorization. Your current spiritual clearance does not grant
            passage to this sacred realm.
          </p>

          <!-- Sacred action buttons -->
          <div class="space-y-4 animate-fadeInUp animation-delay-600">
            <button
              (click)="goBack()"
              class="w-full bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 text-white font-semibold py-4 px-8 rounded-2xl hover:shadow-xl hover:shadow-soulfire/30 dark:hover:shadow-fuchsia-500/30 hover:scale-105 transition-all duration-300 transform flex items-center justify-center gap-3 group"
            >
              <i class="pi pi-arrow-left group-hover:animate-bounce"></i>
              Return to Your Sacred Path
            </button>

            <button
              (click)="toggleModal()"
              class="w-full border-2 border-soulfire/50 dark:border-fuchsia-500/50 bg-white/80 dark:bg-stone-700/80 text-soulfire dark:text-fuchsia-400 font-semibold py-4 px-8 rounded-2xl hover:bg-soulfire dark:hover:bg-fuchsia-600 hover:text-white hover:scale-105 transition-all duration-300 transform flex items-center justify-center gap-3 group backdrop-blur-sm"
            >
              <i class="pi pi-envelope group-hover:animate-pulse"></i>
              Seek Divine Guidance
            </button>
          </div>

          <!-- Sacred wisdom -->
          <div
            class="mt-8 p-4 bg-bloom/10 dark:bg-fuchsia-500/10 rounded-xl animate-fadeInUp animation-delay-800 border border-bloom/20 dark:border-fuchsia-500/20"
          >
            <p
              class="text-sm text-rooted/60 dark:text-stone-400 italic leading-relaxed"
            >
              "Every boundary is a teacher, every limitation a sacred invitation
              to grow deeper in wisdom."
            </p>
          </div>
        </div>
      </div>

      <!-- Sacred guidance modal -->
      @if (showModal) {
      <div
        class="fixed inset-0 bg-rooted/60 dark:bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn"
        (click)="toggleModal()"
      >
        <div
          class="bg-white/95 dark:bg-stone-800/95 backdrop-blur-xl rounded-3xl shadow-2xl dark:shadow-fuchsia-900/20 p-8 w-[90%] max-w-md relative z-50 border border-grounded/20 dark:border-stone-700/50 animate-slideUp"
          (click)="$event.stopPropagation()"
        >
          <!-- Sacred header -->
          <div class="text-center mb-6">
            <div
              class="w-16 h-16 mx-auto bg-gradient-to-br from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 rounded-full flex items-center justify-center mb-4 shadow-lg dark:shadow-fuchsia-500/25"
            >
              <i class="pi pi-heart text-2xl text-white"></i>
            </div>
            <h2
              class="text-2xl font-bold bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-400 dark:to-fuchsia-300 bg-clip-text text-transparent font-sacramento"
            >
              Sacred Guidance Portal
            </h2>
          </div>

          <p
            class="text-rooted/70 dark:text-stone-300 text-center mb-6 leading-relaxed"
          >
            Reach out through these divine channels for spiritual support and
            guidance:
          </p>

          <div class="space-y-4 mb-8">
            <div
              class="bg-sacred/30 dark:bg-stone-700/50 p-4 rounded-xl border border-grounded/20 dark:border-stone-600/50"
            >
              <div class="flex items-center gap-3 mb-2">
                <i
                  class="pi pi-envelope text-soulfire dark:text-fuchsia-400"
                ></i>
                <span class="font-semibold text-rooted dark:text-stone-100"
                  >Sacred Vessel</span
                >
              </div>
              <p class="text-sm text-rooted/70 dark:text-stone-300 ml-6">
                support&#64;coherasthoughtpartner.com
              </p>
            </div>

            <div
              class="bg-sacred/30 dark:bg-stone-700/50 p-4 rounded-xl border border-grounded/20 dark:border-stone-600/50"
            >
              <div class="flex items-center gap-3 mb-2">
                <i class="pi pi-phone text-soulfire dark:text-fuchsia-400"></i>
                <span class="font-semibold text-rooted dark:text-stone-100"
                  >Divine Connection</span
                >
              </div>
              <p class="text-sm text-rooted/70 dark:text-stone-300 ml-6">
                +91-9876543210
              </p>
            </div>

            <div
              class="bg-sacred/30 dark:bg-stone-700/50 p-4 rounded-xl border border-grounded/20 dark:border-stone-600/50"
            >
              <div class="flex items-center gap-3 mb-2">
                <i class="pi pi-clock text-soulfire dark:text-fuchsia-400"></i>
                <span class="font-semibold text-rooted dark:text-stone-100"
                  >Sacred Hours</span
                >
              </div>
              <p class="text-sm text-rooted/70 dark:text-stone-300 ml-6">
                Monday–Friday, 10am–6pm IST
              </p>
            </div>
          </div>

          <button
            (click)="toggleModal()"
            class="w-full bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 text-white py-3 rounded-xl hover:scale-105 transition-all duration-300 font-semibold shadow-lg hover:shadow-soulfire/30 dark:hover:shadow-fuchsia-500/30 flex items-center justify-center gap-2"
          >
            <i class="pi pi-times"></i>
            Close Sacred Portal
          </button>
        </div>
      </div>
      }
    </div>
  `,
  styles: [
    `
      @import url('https://fonts.googleapis.com/css2?family=Sacramento:wght@400&display=swap');

      .font-sacramento {
        font-family: 'Sacramento', cursive;
      }

      .grid-pattern {
        background-image: radial-gradient(
          circle at 1px 1px,
          rgba(139, 69, 19, 0.1) 1px,
          transparent 0
        );
        background-size: 20px 20px;
        width: 100%;
        height: 100%;
      }

      .dark .grid-pattern-dark {
        background-image: radial-gradient(
          circle at 1px 1px,
          rgba(168, 85, 247, 0.1) 1px,
          transparent 0
        );
        background-size: 20px 20px;
        width: 100%;
        height: 100%;
      }

      @keyframes slideUp {
        from {
          transform: translateY(60px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes fadeInUp {
        from {
          transform: translateY(30px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes bounceIn {
        0% {
          transform: scale(0.3);
          opacity: 0;
        }
        50% {
          transform: scale(1.15);
        }
        80% {
          transform: scale(0.95);
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }

      @keyframes float {
        0%,
        100% {
          transform: translateY(0) rotate(0deg);
        }
        33% {
          transform: translateY(-20px) rotate(120deg);
        }
        66% {
          transform: translateY(10px) rotate(240deg);
        }
      }

      @keyframes float-delayed {
        0%,
        100% {
          transform: translateY(0) rotate(0deg);
        }
        33% {
          transform: translateY(-15px) rotate(-90deg);
        }
        66% {
          transform: translateY(8px) rotate(-180deg);
        }
      }

      @keyframes spin-slow {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .animate-slideUp {
        animation: slideUp 1s ease-out;
      }
      .animate-fadeIn {
        animation: fadeIn 0.5s ease-out;
      }
      .animate-fadeInUp {
        animation: fadeInUp 0.8s ease-out forwards;
        opacity: 0;
      }
      .animate-bounceIn {
        animation: bounceIn 1.2s ease-out;
      }
      .animate-float {
        animation: float 8s ease-in-out infinite;
      }
      .animate-float-delayed {
        animation: float-delayed 10s ease-in-out infinite;
      }
      .animate-spin-slow {
        animation: spin-slow linear infinite;
      }

      .animation-delay-200 {
        animation-delay: 0.2s;
      }
      .animation-delay-300 {
        animation-delay: 0.3s;
      }
      .animation-delay-400 {
        animation-delay: 0.4s;
      }
      .animation-delay-500 {
        animation-delay: 0.5s;
      }
      .animation-delay-600 {
        animation-delay: 0.6s;
      }
      .animation-delay-800 {
        animation-delay: 0.8s;
      }

      .bg-clip-text {
        -webkit-background-clip: text;
        background-clip: text;
      }

      /* Custom color classes - you'll need to define these in your Tailwind config */
      .bg-sacred {
        background-color: #f8f4f0;
      }
      .bg-elegant {
        background-color: #fdfcfa;
      }
      .bg-rooted {
        background-color: #8b4513;
      }
      .bg-soulfire {
        background-color: #d97706;
      }
      .bg-bloom {
        background-color: #ec4899;
      }
      .bg-grounded {
        background-color: #6b7280;
      }
      .text-rooted {
        color: #8b4513;
      }
      .text-soulfire {
        color: #d97706;
      }
      .border-soulfire {
        border-color: #d97706;
      }
      .border-bloom {
        border-color: #ec4899;
      }
      .border-grounded {
        border-color: #6b7280;
      }

      /* Dark theme color overrides */
      @media (prefers-color-scheme: dark) {
        .dark .bg-sacred {
          background-color: #374151;
        }
        .dark .bg-elegant {
          background-color: #1f2937;
        }
        .dark .bg-rooted {
          background-color: #a855f7;
        }
        .dark .bg-soulfire {
          background-color: #a855f7;
        }
        .dark .bg-bloom {
          background-color: #c084fc;
        }
        .dark .bg-grounded {
          background-color: #6b7280;
        }
        .dark .text-rooted {
          color: #f3f4f6;
        }
        .dark .text-soulfire {
          color: #c084fc;
        }
        .dark .border-soulfire {
          border-color: #a855f7;
        }
        .dark .border-bloom {
          border-color: #c084fc;
        }
        .dark .border-grounded {
          border-color: #6b7280;
        }
      }
    `,
  ],
})
export class UnauthorizedComponent {
  showModal = false;

  constructor(private router: Router) {}

  goBack() {
    this.router.navigate(['/dashboard']);
  }

  toggleModal() {
    this.showModal = !this.showModal;
  }
}
